import { Component, ErrorInfo, ReactNode, lazy, Suspense, useEffect, useState } from "react";
import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/layout";
import { AuthProvider, useAuth } from "@/context/auth";
import { BranchProvider, useBranch } from "@/context/branch";
import { ThemeProvider } from "@/context/theme";
import NotFound from "@/pages/not-found";

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("App ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: "monospace", background: "#fff", minHeight: "100vh" }}>
          <h2 style={{ color: "#c00", marginBottom: 16 }}>Something went wrong</h2>
          <pre style={{ background: "#f5f5f5", padding: 16, borderRadius: 8, overflow: "auto", fontSize: 13, color: "#333" }}>
            {this.state.error.message}
            {"\n\n"}
            {this.state.error.stack}
          </pre>
          <button
            style={{ marginTop: 16, padding: "8px 16px", background: "#1a56db", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import Dashboard from "@/pages/dashboard";
import Inventory from "@/pages/inventory";
import ProductMatrixEntry from "@/pages/product-matrix-entry";
import Customers from "@/pages/customers";
import CustomerGroups from "@/pages/customer-groups";
import Unpaid from "@/pages/unpaid";
import PaymentHub from "@/pages/payment-hub";
import SupplierLedger from "@/pages/supplier-ledger";
import SupplierBills from "@/pages/supplier-bills";
import FundTransfers from "@/pages/fund-transfers";
import SmsPage from "@/pages/sms";
import VouchersPage from "@/pages/vouchers";

import AccountingPayments from "@/pages/accounting-payments";
import AccountingExpenseReports from "@/pages/accounting-expense-reports";
import AccountingPaymentReports from "@/pages/accounting-payment-reports";
import AccountingProfitLoss from "@/pages/accounting-profit-loss";
import AccountingBudgets from "@/pages/accounting-budgets";
import AccountingBalanceSheet from "@/pages/accounting-balance-sheet";
import AccountingCashFlow from "@/pages/accounting-cash-flow";
import BankReconciliation from "@/pages/bank-reconciliation";

import PeopleStaffPerformance from "@/pages/people-staff-performance";
import PeopleSupplierReports from "@/pages/people-supplier-reports";
import PeopleCustomerReports from "@/pages/people-customer-reports";
import PeopleLoyalty from "@/pages/people-loyalty";
import Categories from "@/pages/categories";
import Brands from "@/pages/brands";
import Sales from "@/pages/sales";
const NewSale = lazy(() => import("@/pages/new-sale"));
import ReceiptPreview from "@/pages/receipt-preview";
import Purchases from "@/pages/purchases";
import PurchaseReturns from "@/pages/purchase-returns";
import ProductBundles from "@/pages/product-bundles";
import Suppliers from "@/pages/suppliers";
import Staff from "@/pages/staff";
import SchoolFees from "@/pages/school-fees";
import Expenses from "@/pages/expenses";
import Returns from "@/pages/returns";
import Reports from "@/pages/reports";
import Admin from "@/pages/admin";
import PartnerAdsPage from "@/pages/partner-ads";
import { PartnerAdsDashboardWidget } from "@/components/partner-ads-dashboard-widget";
import Settings from "@/pages/settings";
import { MerchantDataRequests } from "@/components/merchant-data-requests";
import Attendance from "@/pages/attendance";
import Checklists from "@/pages/checklists";
import Addons from "@/pages/addons";
import Login, { Demo } from "@/pages/login";
import BusinessLogin from "@/pages/business-login";
import Signup from "@/pages/signup";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import Unsubscribe from "@/pages/unsubscribe";
import Subscribe from "@/pages/subscribe";
import Maintenance from "@/pages/maintenance";
import Landing from "@/pages/landing";
import Changelog from "@/pages/changelog";
import Roadmap from "@/pages/roadmap";
import Careers from "@/pages/careers";
import PrivacyPolicy from "@/pages/privacy-policy";
import TermsOfService from "@/pages/terms-of-service";
import CookiePolicy from "@/pages/cookie-policy";
import RefundPolicy from "@/pages/refund-policy";
import Help from "@/pages/help";
import Contact from "@/pages/contact";
import Branches from "@/pages/branches";
import StockTransfers from "@/pages/stock-transfers";
import StockMovements from "@/pages/stock-movements";
import PriceChecker from "@/pages/price-checker";
import ProductSalesReport from "@/pages/product-sales-report";
import GiftRegistry from "@/pages/gift-registry";
import EcommerceComingSoon from "@/pages/ecommerce-coming-soon";
import EcommercePage from "@/pages/ecommerce";
import Storefront from "@/pages/storefront";
import Marketplace from "@/pages/marketplace";
import SupermarketDashboard from "@/pages/dashboards/supermarket";
import FashionDashboard from "@/pages/dashboards/fashion";
import FoodDashboard from "@/pages/dashboards/food";
import ReadyToWearDashboard from "@/pages/dashboards/ready-to-wear";
import SalonDashboard from "@/pages/dashboards/salon";
import TailorDashboard from "@/pages/dashboards/tailor";
import FabricTextileDashboard from "@/pages/dashboards/fabric-textile";
import Shifts from "@/pages/shifts";
import Payroll from "@/pages/payroll";
import { OnboardingChecklist } from "@/components/onboarding-checklist";
import { WelcomeModal } from "@/components/welcome-modal";
import { Loader2 } from "lucide-react";
import { OfflineSyncStatus } from "@/components/offline-sync-status";
import {
  listOfflineSales,
  OFFLINE_SALES_EVENT,
  reportOfflineSalesError,
  syncOfflineSales,
} from "@/lib/offline-sales";

function handle401(error: unknown) {
  if (error && typeof error === "object" && "status" in error && (error as any).status === 401) {
    const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
    fetch(`${BASE}/api/auth/logout`, { method: "POST", credentials: "include" }).finally(() => {
      window.location.href = (import.meta.env.BASE_URL || "/") + "login";
    });
  }
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
  queryCache: new QueryCache({ onError: handle401 }),
  mutationCache: new MutationCache({ onError: handle401 }),
});

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function PermissionRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
  const { user } = useAuth();
  const allowed = user?.role === "admin" || user?.role === "superadmin" || user?.permissions?.includes(permission);
  return allowed ? <>{children}</> : <Redirect to="/" />;
}

function OfflineSalesSync() {
  const { store, user, offlineRestored, offlineEnabled, prepareOffline } = useAuth();
  const { activeBranchId, branchReady } = useBranch();

  useEffect(() => {
    if (!store?.id || !user?.id || offlineRestored || !branchReady) return;
    const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");
    const synchronize = async () => {
      if (!navigator.onLine) return;
      try {
        const queued = await listOfflineSales(store.id, user.id);
        if (queued.length === 0) return;
        const result = await syncOfflineSales(store.id, user.id, basePath);
        if (result.total === 0) {
          await queryClient.invalidateQueries();
          if (offlineEnabled) {
            await prepareOffline(activeBranchId).catch(error =>
              console.warn("Saved offline catalogue could not be refreshed after syncing:", error));
          }
        }
        window.dispatchEvent(new CustomEvent(OFFLINE_SALES_EVENT));
      } catch (error) {
        console.error("Could not automatically synchronize queued offline sales:", error);
        reportOfflineSalesError();
      }
    };
    const onOnline = () => { void synchronize(); };
    window.addEventListener("online", onOnline);
    void synchronize();
    return () => window.removeEventListener("online", onOnline);
  }, [store?.id, user?.id, offlineRestored, offlineEnabled, activeBranchId, branchReady]);

  return null;
}

function SmartDashboard() {
  const { user } = useAuth();
  let content: React.ReactNode;
  switch (user?.businessType) {
    case "school_campus":
      content = user?.role === "admin" || user?.role === "superadmin" || user?.permissions?.includes("school_fees")
        ? <Redirect to="/school-fees" />
        : <Dashboard />;
      break;
    case "supermarket": content = <SupermarketDashboard />; break;
    case "fashion":     content = <FashionDashboard />; break;
    case "food":        content = <FoodDashboard />; break;
    case "ready_to_wear": content = <ReadyToWearDashboard />; break;
    case "salon":       content = <SalonDashboard />; break;
    case "tailor":          content = <TailorDashboard />; break;
    case "fabric_textile":  content = <FabricTextileDashboard />; break;
    default:                content = <Dashboard />; break;
  }
  return (
    <>
      <WelcomeModal />
      <OnboardingChecklist />
      {content}
      {user?.businessType !== "school_campus" && <PartnerAdsDashboardWidget />}
    </>
  );
}

function AppRoutes() {
  const { isAuthenticated, isLoading, hasActiveSubscription, user, offlineRestored } = useAuth();
  const [location] = useLocation();
  // Keep the checkout subtree mounted when revalidation restores online access.
  const [offlineWorkspaceOpen, setOfflineWorkspaceOpen] = useState(false);
  useEffect(() => {
    if (offlineRestored) setOfflineWorkspaceOpen(true);
    else if (!isLoading && !isAuthenticated) setOfflineWorkspaceOpen(false);
  }, [offlineRestored, isLoading, isAuthenticated]);

  // Email recipients can confirm without a session or an active subscription.
  if (location === "/unsubscribe") return <Unsubscribe />;
  if (isLoading) return <LoadingScreen />;

  // The marketplace is public and should look the same for shoppers and merchants.
  if (location === "/shop" || location === "/shop/") return <Marketplace />;

  if ((offlineRestored || offlineWorkspaceOpen) && isAuthenticated && hasActiveSubscription) {
    return <BranchProvider>
      <OfflineSalesSync />
      <OfflineSyncStatus />
      <main className="mx-auto max-w-6xl space-y-4 p-4 md:p-8" data-testid="offline-pos-workspace">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
          {offlineRestored
            ? <><strong>Offline POS only.</strong> Your previously verified access is temporary. Admin, inventory editing, payments and reports need an online sign-in.</>
            : <><strong>Online access restored.</strong> Continue your checkout here.{" "}
                <a className="underline font-semibold" href={import.meta.env.BASE_URL || "/"}
                  onClick={event => { if (!window.confirm("Finish your current checkout first. Returning to the full workspace will clear any unfinished cart. Continue?")) event.preventDefault(); }}>
                  Return to full workspace
                </a>
              </>}
        </div>
        <Suspense fallback={<LoadingScreen />}><NewSale /></Suspense>
      </main>
    </BranchProvider>;
  }

  if (!isAuthenticated) {
    return (
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/changelog" component={Changelog} />
        <Route path="/roadmap" component={Roadmap} />
        <Route path="/careers" component={Careers} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/cookie-policy" component={CookiePolicy} />
        <Route path="/refund-policy" component={RefundPolicy} />
        <Route path="/help" component={Help} />
        <Route path="/contact" component={Contact} />
        <Route path="/branches" component={Branches} />
        <Route path="/stock-transfers" component={StockTransfers} />
        <Route path="/login" component={Login} />
        <Route path="/demo" component={Demo} />
        <Route path="/login/:type" component={BusinessLogin} />
        <Route path="/signup" component={Signup} />
        <Route path="/shop/coming-soon" component={EcommerceComingSoon} />
        <Route path="/shop/:slug" component={Storefront} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route component={() => <Redirect to="/" />} />
      </Switch>
    );
  }

  if (!hasActiveSubscription) {
    // Keep public entry and account-recovery routes available even when an old
    // authenticated session belongs to a store without active access.
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";
    return (
      <Switch>
        <Route path="/" component={Landing} />
        <Route path="/login" component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/subscribe" component={Subscribe} />
        {user?.role === "admin" && <Route path="/data-requests"><div className="max-w-2xl mx-auto p-6"><MerchantDataRequests /></div></Route>}
        {isAdmin && (
          <Route path="/ecommerce">
            <BranchProvider>
              <Layout><EcommercePage /></Layout>
            </BranchProvider>
          </Route>
        )}
        {isAdmin
          ? <Route component={() => <Redirect to="/subscribe" />} />
          : <Route component={Maintenance} />}
      </Switch>
    );
  }

  return (
    <ErrorBoundary>
    <BranchProvider>
    <OfflineSalesSync />
    <Layout>
      <Switch>
        {/* Core — SmartDashboard routes based on businessType */}
        <Route path="/" component={SmartDashboard} />
        <Route path="/reports" component={Reports} />
        <Route path="/reports/product-sales" component={ProductSalesReport} />

        {/* Inventory */}
        <Route path="/inventory/matrix-entry" component={ProductMatrixEntry} />
        <Route path="/inventory" component={Inventory} />
        <Route path="/stock-movements" component={StockMovements} />
        <Route path="/categories" component={Categories} />
        <Route path="/brands" component={Brands} />
        <Route path="/purchases" component={Purchases} />
        <Route path="/shifts" component={Shifts} />
        <Route path="/purchase-returns" component={PurchaseReturns} />
        <Route path="/product-bundles" component={ProductBundles} />
        <Route path="/returns" component={Returns} />

        {/* Sales */}
        <Route path="/sales/new">
          <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
            <NewSale />
          </Suspense>
        </Route>
        <Route path="/sales/price-checker">
          <PermissionRoute permission="price_checker"><PriceChecker /></PermissionRoute>
        </Route>
        <Route path="/sales" component={Sales} />
        <Route path="/receipt-preview" component={ReceiptPreview} />

        {/* People — Staff */}
        <Route path="/staff" component={Staff} />
        <Route path="/attendance" component={Attendance} />
        <Route path="/payroll" component={Payroll} />
        <Route path="/people/staff-performance" component={PeopleStaffPerformance} />

        {/* People — Customers */}
        <Route path="/customers" component={Customers} />
        <Route path="/school-fees">
          <PermissionRoute permission="school_fees"><SchoolFees /></PermissionRoute>
        </Route>
        <Route path="/customer-groups" component={CustomerGroups} />
        <Route path="/customer-invoices" component={Unpaid} />
        <Route path="/unpaid" component={Unpaid} />
        <Route path="/people/loyalty" component={PeopleLoyalty} />
        <Route path="/people/customer-reports" component={PeopleCustomerReports} />

        {/* People — Suppliers */}
        <Route path="/suppliers" component={Suppliers} />
        <Route path="/people/supplier-reports" component={PeopleSupplierReports} />

        {/* Payment Hub */}
        <Route path="/payment-hub" component={PaymentHub} />
        <Route path="/supplier-ledger" component={SupplierLedger} />
        <Route path="/supplier-bills" component={SupplierBills} />
        <Route path="/fund-transfers" component={FundTransfers} />
        <Route path="/sms" component={SmsPage} />
        <Route path="/vouchers" component={VouchersPage} />

        {/* Accounting */}
        <Route path="/expenses" component={Expenses} />
        <Route path="/accounting/payments" component={AccountingPayments} />
        <Route path="/accounting/bank-reconciliation">
          <PermissionRoute permission="accounting"><BankReconciliation /></PermissionRoute>
        </Route>
        <Route path="/accounting/expense-reports" component={AccountingExpenseReports} />
        <Route path="/accounting/payment-reports" component={AccountingPaymentReports} />
        <Route path="/accounting/profit-loss" component={AccountingProfitLoss} />
        <Route path="/accounting/budgets" component={AccountingBudgets} />
        <Route path="/accounting/balance-sheet" component={AccountingBalanceSheet} />
        <Route path="/accounting/cash-flow" component={AccountingCashFlow} />

        {/* Gift Registry */}
        <Route path="/gift-registry" component={GiftRegistry} />

        {/* Branches & Stock Transfers */}
        <Route path="/branches" component={Branches} />
        <Route path="/stock-transfers" component={StockTransfers} />

        {/* Enterprise */}
        <Route path="/checklists" component={Checklists} />
        <Route path="/addons" component={Addons} />

        {/* E-commerce / Online Store */}
        <Route path="/ecommerce" component={EcommercePage} />
        <Route path="/shop/coming-soon" component={EcommerceComingSoon} />
        <Route path="/shop/:slug" component={Storefront} />

        {/* Admin */}
        <Route path="/admin" component={Admin} />
        <Route path="/partner-ads" component={PartnerAdsPage} />
        <Route path="/settings" component={Settings} />
        <Route path="/data-requests" component={() => <div className="max-w-2xl mx-auto p-6"><MerchantDataRequests /></div>} />
        <Route path="/subscribe" component={Subscribe} />

        <Route component={NotFound} />
      </Switch>
    </Layout>
    </BranchProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
